import React, {useCallback, useState} from 'react';
import {useLocation} from "react-router-dom";
import ReactModal from "react-modal";
import {CellProps, Column} from "react-table";
import {ListTableWithSearchBox, SearchParams} from "../../common/table/ListTableWithSearchBox";
import {Label} from "../../../../types/admin/label";
import {labelApi} from "../../../../apis/admin/menu/labelApi";
import useAppRouter from "../../../../hooks/useAppRouter";
import {logError} from "../../../../libs/utils/logError";
import {formatOmittedDateTime} from "../../../../libs/utils/formatDate";

interface DataType extends Label {
  operation?: string
}

interface SelectLabelModalProps {
  labelSetId: number  | undefined;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  onSelect: (label: Label) => Promise<void>;
  onSelected: () => Promise<void>;
}

export const SelectLabelModal = (props: SelectLabelModalProps) => {
  const {labelSetId, setShowModal, onSelect, onSelected} = props;
  const [labelList, setLabelList] = useState<DataType[]>([]);
  const [abortController, setAbortController] = React.useState<AbortController | undefined>();
  const [totalCount, setTotalCount] = React.useState<number>(10);
  const { goto } = useAppRouter();
  const location = useLocation();

  const sortFunc = async (params: SearchParams) => {
    setAbortController(new AbortController());
    try
    {
      const offset = (params.currentPage - 1) * params.size;
      const response = await labelApi.index(labelSetId, params.keyword, params.size, offset, params.sort, { abortSignal: abortController?.signal });
      setAbortController(undefined);
      setLabelList(response.labelList);
      setTotalCount(response.totalHits);
    } catch(e) {
      await logError(
        location.pathname,
        'ラベル一覧の取得に失敗しました。',
        e
      );

      goto('internalServerError');
    }
  };

  const initialSort = [
    'createdAt:desc'
  ];

  const DateTimeCell = useCallback(({ value }: CellProps<DataType>) => (
    <div>{ formatOmittedDateTime(value) }</div>
  ), []);

  const OperationCell = useCallback(({ row }: CellProps<DataType>) => {
    const handleSelectClick = async (label: Label): Promise<void> => {
      await onSelect(label);
      await onSelected();
    };

    return (
      <>
        <button
          className="btn-primary"
          type='button'
          onClick={async () => {
             await handleSelectClick(row.original);
          }}
        >
          選 択
        </button>
      </>
    );
  }, [onSelect, onSelected]);

  const columns: Column<DataType>[] = React.useMemo(
    () => [
      {
        Header: '名称',
        accessor: 'name',
        className: 'select-none',
      },
      {
        Header: '作成日時',
        accessor: 'createdAt',
        Cell: DateTimeCell,
      },
      {
        Header: '操作',
        accessor: 'operation',
        Cell: OperationCell,
        disableSortBy: true,
      },
    ],

    [OperationCell, DateTimeCell]
  );

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => setShowModal(false)}
        className="select-modal"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">ラベル選択</h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setShowModal(false)}
            >
              ×
            </button>
          </div>
          <div className="form-modal-content">
            <div className="p-4">
              <ListTableWithSearchBox
                {...{
                  tableData: labelList,
                  columns,
                  initialSort,
                  totalCount,
                  sortFunc,
                }}
              />
            </div>
          </div>
          <div className="form-modal-footer-container">
            <div className="form-modal-footer">
              <button
                className="btn-sub"
                type="button"
                onClick={() => setShowModal(false)}
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>

      </ReactModal>
    </>
  );
};
